import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Accordion from './components/Accordion';
import { partners, categories, itemList } from './utils/data';

const SELECTED_IDS_KEY = 'coffeeList_selectedIds';
const OPEN_CATEGORIES_KEY = 'coffeeList_openCategories';

const loadSet = (key) => {
  try {
    const saved = localStorage.getItem(key);
    if (saved) return new Set(JSON.parse(saved));
  } catch {
    // ignore corrupt storage
  }
  return new Set();
};

const loadOpenCategories = () => {
  try {
    const saved = localStorage.getItem(OPEN_CATEGORIES_KEY);
    if (saved) return JSON.parse(saved);
  } catch {
    // ignore corrupt storage
  }
  return Object.fromEntries(categories.map((c) => [c.id, false]));
};

const App = () => {
  const [selectedIds, setSelectedIds] = useState(() => loadSet(SELECTED_IDS_KEY));
  const [openCategories, setOpenCategories] = useState(loadOpenCategories);
  const [error, setError] = useState('');

  const itemsByCategory = useMemo(() => {
    const map = {};
    for (const item of itemList) {
      if (!map[item.categoryId]) map[item.categoryId] = [];
      map[item.categoryId].push(item);
    }
    return map;
  }, []);

  useEffect(() => {
    localStorage.setItem(SELECTED_IDS_KEY, JSON.stringify([...selectedIds]));
  }, [selectedIds]);

  useEffect(() => {
    localStorage.setItem(OPEN_CATEGORIES_KEY, JSON.stringify(openCategories));
  }, [openCategories]);

  const handleAccordion = useCallback((categoryId) => {
    setOpenCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  }, []);

  const handleToggle = useCallback((itemId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
    setError('');
  }, []);

  const selectedItems = useMemo(
    () => itemList.filter((item) => selectedIds.has(item.id)),
    [selectedIds]
  );

  const sendList = useCallback(
    (partner) => {
      if (selectedIds.size === 0) {
        setError('Selecciona al menos un artículo antes de enviar.');
        return;
      }

      const messageBody = selectedItems.map((item) => item.messageText).join(', ');
      const message = `*Te comparto la lista de faltantes:*\n\n${messageBody}`;
      const url = `https://api.whatsapp.com/send?phone=${partner.phone}&text=${encodeURIComponent(
        message
      )}&app_absent=0`;

      window.open(url, '_blank', 'noopener,noreferrer');
      setSelectedIds(new Set());
    },
    [selectedIds.size, selectedItems]
  );

  const clearList = useCallback(() => {
    setSelectedIds(new Set());
    setError('');
  }, []);

  return (
    <div>
      <div className="main--card--background">
        <main className="main--card">
          <h1>Lista de Compras y Actividades de la Cafetería</h1>
          {error && <p className="error-message" role="alert">{error}</p>}
          {categories.map((category) => (
            <Accordion
              key={category.id}
              category={category}
              items={itemsByCategory[category.id] || []}
              isOpen={!!openCategories[category.id]}
              selectedIds={selectedIds}
              onToggle={handleToggle}
              onToggleAccordion={handleAccordion}
            />
          ))}
          <div className="main--actions">
            <button
              className="main--card--button"
              onClick={clearList}
              type="button"
            >
              Limpiar lista
            </button>
            {partners.map((partner) => (
              <button
                key={partner.name}
                className="main--card--button"
                onClick={() => sendList(partner)}
                type="button"
              >
                Enviar a {partner.name}
              </button>
            ))}
          </div>
        </main>
      </div>
    </div>
  );
};

export default App;
